import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useAtomValue } from "jotai";
import { format } from "date-fns";
import {
  Button,
  Checkbox,
  Spinner,
  Tooltip,
  Link,
} from "@fluentui/react-components";
import {
  InfoRegular,
  CheckmarkRegular,
  DismissRegular,
  ErrorCircleRegular,
  ShieldRegular,
  MailArrowUpRegular,
  ReceiptMoneyRegular,
} from "@fluentui/react-icons";
import {
  useCreateOfferMutation,
  useSaveOfferMutation,
  useUpdateOfferMutation,
} from "state/queries/useOffers";
import type { QuoteUpdateDto, ExcludedItemDto } from "library/types/types";
import { useCalculationMutation } from "state/queries/useCalculations";
import { transformProposalToCalculationRequest } from "utils/proposalTransformers";
import { ValidationSummary } from "components/ValidationSummary";
import { useGetTaxonomies } from "state/queries/useGetTaxonomies";
import InsuranceDataSection from "./sections/InsuranceDataSection";
import MtplQuotesDisplay from "./components/MtplQuotesDisplay";
import { SendEmailDialog } from "components/SendEmailDialog";
import { GroupInvoiceDocuments } from "components/GroupInvoiceDocuments";
import {
  MTPL_EMAIL_TEMPLATE,
  MTPL_INVOICE_EMAIL_TEMPLATE,
  DEFAULT_SERVICE_PERSON_PHONE,
} from "library/constants/emailTemplates";
import { formatDateTimeString } from "library/utils";
import { userInfoState } from "store/index";
import { QuoteErrorBoundary } from "components/QuoteErrorBoundary";
import { ErrorDetailsDialog } from "components/ErrorDetailsDialog";
import { CreateInvoiceDialog } from "components/CreateInvoiceDialog";
import { getInvoiceEmailAddressee } from "components/InvoicePayer";
import { QUERY_KEYS } from "library/shared";

// Styled Components
const TabWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 17px 0 6px 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FooterPanel = styled.div`
  flex-shrink: 0;
  background: ${({ theme }) => theme.semanticColors.bodyBackground};
  border-top: 1px solid ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
`;

const QuotesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SelectionSection = styled.div`
  display: flex;
  gap: 16px;
  align-items: stretch;
  margin-bottom: 12px;
`;

const SelectionPanel = styled.div`
  background: ${({ theme }) => theme.semanticColors.bodyStandoutBackground};
  border: 1px solid ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  border-radius: 8px;
  padding: 22px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SelectionLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const SelectionLabel = styled.label`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.semanticColors.bodyText};
`;

const SelectionActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CheckboxList = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  column-gap: 16px;
`;

const ErrorSection = styled.div`
  background: ${({ theme }) =>
    theme.isDark ? "rgba(255, 77, 79, 0.15)" : "#fff2f0"};
  border: 1px solid
    ${({ theme }) => (theme.isDark ? "rgba(255, 77, 79, 0.3)" : "#ffccc7")};
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  .error-icon {
    font-size: 20px;
    color: ${({ theme }) => (theme.isDark ? "#ff7875" : "#ff4d4f")};
  }

  .error-content {
    flex: 1;

    strong {
      color: ${({ theme }) => (theme.isDark ? "#ff7875" : "#a8071a")};
    }

    .error-description {
      font-size: 14px;
      color: ${({ theme }) => (theme.isDark ? "#bfbfbf" : "#595959")};
      margin-top: 4px;
    }
  }

  .error-button {
    background-color: ${({ theme }) => (theme.isDark ? "#ff4d4f" : "#ff4d4f")};
    border-color: ${({ theme }) => (theme.isDark ? "#ff7875" : "#ff4d4f")};

    &:hover {
      background-color: ${({ theme }) =>
        theme.isDark ? "#ff7875" : "#ff7a7a"};
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-left: auto;
`;

const ErrorCountText = styled.span`
  cursor: pointer;
  color: ${({ theme }) => (theme.isDark ? "#ff7875" : "#ff4d4f")};
  font-weight: 600;
  font-size: 14px;
  user-select: none;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => (theme.isDark ? "#ff9c9c" : "#ff7a7a")};
    text-decoration: underline;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const DocumentsSection = styled.div`
  background: ${({ theme }) => theme.semanticColors.bodyStandoutBackground};
  border: 1px solid ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  border-radius: 8px;
  padding: 22px;
  margin-top: 0;
  margin-bottom: 12px;
`;

const DocumentsTitle = styled.h3`
  margin: 0 0 12px 0;
  fontsize: 14px;
  fontweight: 600;
  color: ${({ theme }) => theme.semanticColors.bodyText};
`;

const DocumentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
`;

const FileInfoBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  border-radius: 8px;
  padding: 12px;
  background: ${({ theme }) => theme.semanticColors.bodyBackground};
`;

const FileInfoHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
`;

const EmailButtonContainer = styled.div`
  display: flex;
  align-items: center;
`;

const DocumentLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: ${({ theme }) => theme.semanticColors.link};
  flex: 1;

  &:hover {
    color: ${({ theme }) => theme.semanticColors.linkHovered};
  }
`;

const NoDocumentsMessage = styled.div`
  padding: 32px;
  text-align: center;
  color: ${({ theme }) => theme.semanticColors.bodySubtext};
  background: ${({ theme }) => theme.semanticColors.bodyBackground};
  border: 2px dashed ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  border-radius: 8px;
  font-size: 14px;
`;

const EmailSentInfo = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: ${({ theme }) =>
    theme.semanticColors.cardStandoutBackground ||
    theme.semanticColors.bodyFrameBackground ||
    "#f3f2f1"};
  border-left: 3px solid
    ${({ theme }) => theme.palette.themePrimary || "#0078d4"};
  border-radius: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.semanticColors.bodyText};
  line-height: 1.5;
`;

// Types
interface MtplInsurer {
  id: string;
  name: string;
  displayName: string;
}

interface QuotePeriod {
  id: string;
  label: string;
  months: number;
}

interface MtplPremium {
  Year: number;
  Payments: number;
  ClientPremium: number;
  InsurerPremium: number;
  InsurerDiscount: number;
  CommissionDiscount: number;
  DiscountDescription: string | null;
  Period: number;
  Id: string | null;
  ConvenienceFee: number | null;
  QuoteId: string | null;
}

interface MtplQuoteData {
  HasPrices: boolean;
  BonusMalus: string | null;
  Premiums: MtplPremium[];
  Insurer: string;
  Calculator: string;
  ErrorList: Array<{
    Code: number;
    Description: string | null;
    Message: string;
  }>;
  Language: number;
  Duration: number;
  ExceptionList: Array<{
    Code: number;
    GenericMessage: string;
    GenericStackTrace?: string;
  }>;
  InsurerRequest: string[];
  InsurerResponse: string[];
  HasErrors: boolean;
  HasInsurerMessages: boolean;
  HasInsurerWarnings: boolean;
  HasInsurerErrors: boolean;
  HasGenericErrors: boolean;
  HasValidationErrors: boolean;
}

interface MtplQuote {
  insurer: string;
  bonusMalus?: string;
  premiums: { [period: number]: number };
  hasError: boolean;
  hasPrices?: boolean; // Separate flag for data availability (not dependent on errors)
  errorMessage?: string;
  isLoading?: boolean;
  referenceNumber?: string;
  // Extended fields from API response
  netPremium?: number;
  taxes?: number;
  fees?: number;
  calculationDuration?: number;
  coefficients?: Array<{
    name: string;
    value: number;
    description?: string;
  }>;
  discounts?: Array<{
    name: string;
    amount: number;
    percentage: number;
  }>;
  // Detailed error and exception information (same as CASCO)
  errorList?: Array<{
    message: string;
    code: number;
  }>;
  exceptionList?: Array<{
    genericMessage: string;
    code: number;
    genericStackTrace?: string;
  }>;
}

interface MtplQuotesTabProps {
  offerData?: any;
  onCalculationStateChange?: (isCalculating: boolean) => void;
  onOfferIdChange?: (offerId: string | null) => void;
  onOfferDataUpdate?: (updatedOffer: any) => void;
  // Shared validation props from parent
  validateRequest: (request: any) => { isValid: boolean; errors: any[] };
  validationErrors: any[];
  isValid: boolean;
  clearErrors: () => void;
  scrollToFirstError: () => void;
  getError: (fieldPath: string) => string | undefined;
  // Quotes state from parent to persist across tab switches
  quotes: MtplQuote[];
  setQuotes: React.Dispatch<React.SetStateAction<MtplQuote[]>>;
  // Raw MTPL data for display component
  rawMtplData: any[];
  setRawMtplData: React.Dispatch<React.SetStateAction<any[]>>;
  isNewProposal?: boolean; // Flag to indicate if this is a new proposal (including duplicates)
}

// Constants
const MTPL_INSURERS: MtplInsurer[] = [
  { id: "Balcia", name: "Balcia", displayName: "Balcia" },
  { id: "Balta", name: "Balta", displayName: "Balta" },
  { id: "BAN", name: "BAN", displayName: "BAN" },
  { id: "BTA", name: "BTA", displayName: "BTA" },
  { id: "Compensa", name: "Compensa", displayName: "Compensa" },
  { id: "Ergo", name: "Ergo", displayName: "Ergo" },
  { id: "Gjensidige", name: "Gjensidige", displayName: "Gjensidige" },
  { id: "IF", name: "IF", displayName: "IF" },
];

const QUOTE_PERIODS: QuotePeriod[] = [
  { id: "1month", label: "1 month", months: 1 },
  { id: "3months", label: "3 months", months: 3 },
  { id: "6months", label: "6 months", months: 6 },
  { id: "9months", label: "9 months", months: 9 },
  { id: "12months", label: "12 months", months: 12 },
];

const MtplQuotesTab: React.FC<MtplQuotesTabProps> = ({
  offerData,
  onCalculationStateChange,
  onOfferIdChange,
  onOfferDataUpdate,
  validateRequest,
  validationErrors,
  isValid,
  clearErrors,
  scrollToFirstError,
  getError,
  quotes,
  setQuotes,
  rawMtplData,
  setRawMtplData,
  isNewProposal = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getValues, watch, setValue } = useFormContext();
  // Don't fetch - just use cached data from App.tsx
  const { data: taxonomy } = useGetTaxonomies(false);
  const userInfo = useAtomValue(userInfoState);

  // Check if user has admin or read-only admin role

  // Check if Calculate button should be hidden via environment variable
  const hideCalculateButton =
    process.env.REACT_APP_HIDE_CALCULATE_BUTTON === "true";

  // Use form state instead of local state for persistence across tab switches
  const selectedInsurersRaw = watch("mtplSelections.selectedInsurers");
  const selectedPeriodsRaw = watch("mtplSelections.selectedPeriods");

  // Stabilize values with useMemo to prevent unnecessary re-renders
  const selectedInsurers = useMemo(
    () => selectedInsurersRaw || [],
    [selectedInsurersRaw],
  );
  const selectedPeriods = useMemo(
    () => selectedPeriodsRaw || [],
    [selectedPeriodsRaw],
  );

  // State - quotes are now passed as props from parent
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [hasLoadedFromOffer, setHasLoadedFromOffer] = useState(false);
  // rawMtplData and setRawMtplData are now passed as props from parent to persist across tab switches
  const [isSavedData, setIsSavedData] = useState<boolean>(false); // Track if viewing saved data
  const [isCalculating, setIsCalculating] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isInvoiceEmailDialogOpen, setIsInvoiceEmailDialogOpen] =
    useState(false);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [selectedQuoteForInvoice, setSelectedQuoteForInvoice] = useState<{
    insurer: string;
    premium: number;
    payments: number;
    period?: number;
    insurerDiscount?: number;
    commissionDiscount?: number;
  } | null>(null);

  // Track pending quote updates (local state until "Update Offer" is clicked)
  const [mtplUpdated, setMtplUpdated] = useState<QuoteUpdateDto[]>([]);
  // Track saved updates (sent to backend, used to detect new changes)
  const [mtplSavedUpdates, setMtplSavedUpdates] = useState<QuoteUpdateDto[]>(
    [],
  );
  // Track excluded insurers for invoice generation
  const [mtplExcluded, setMtplExcluded] = useState<ExcludedItemDto[]>([]);
  // Track saved exclusions (sent to backend, used to detect new changes)
  const [mtplSavedExclusions, setMtplSavedExclusions] = useState<
    ExcludedItemDto[]
  >([]);

  // Store document paths in form state so they persist across tab switches
  // Watch specific fields to prevent unnecessary re-renders
  const offerPdfPathRaw = watch("mtplDocuments.offerPdfPath");
  const offerDocxPathRaw = watch("mtplDocuments.offerDocxPath");
  const savedPdfPathRaw = watch("mtplDocuments.savedPdfPath");
  const savedDocxPathRaw = watch("mtplDocuments.savedDocxPath");
  const invoicePdfPathRaw = watch("mtplDocuments.invoicePdfPath");
  const invoiceDocxPathRaw = watch("mtplDocuments.invoiceDocxPath");
  const offerIdRaw = watch("proposalData.offerId");

  // Memoize to prevent re-renders when values haven't changed
  const offerPdfPath = useMemo(
    () => offerPdfPathRaw || null,
    [offerPdfPathRaw],
  );
  const offerDocxPath = useMemo(
    () => offerDocxPathRaw || null,
    [offerDocxPathRaw],
  );
  const savedPdfPath = useMemo(
    () => savedPdfPathRaw || null,
    [savedPdfPathRaw],
  );
  const savedDocxPath = useMemo(
    () => savedDocxPathRaw || null,
    [savedDocxPathRaw],
  );
  const invoicePdfPath = useMemo(
    () => invoicePdfPathRaw || null,
    [invoicePdfPathRaw],
  );
  const invoiceDocxPath = useMemo(
    () => invoiceDocxPathRaw || null,
    [invoiceDocxPathRaw],
  );
  const offerId = useMemo(() => offerIdRaw || null, [offerIdRaw]);

  // salutation and recipient of the invoice e-mail: the payer of the invoice (the client for invoices without a stored payer)
  const isClientPerson = getValues("clientData.clientType") === "person";
  const invoiceEmailAddressee = getInvoiceEmailAddressee(
    offerData?.Invoice?.Mtpl?.Request?.Payer,
    {
      isPerson: isClientPerson,
      name: isClientPerson
        ? `${getValues("clientData.clientFirstName") || ""} ${
            getValues("clientData.clientLastName") || ""
          }`.trim()
        : getValues("clientData.clientName") || "",
      email: getValues("clientData.clientEmail") || "",
    },
  );

  // Extract invoice data if invoice was created for MTPL
  const mtplInvoiceData = useMemo(() => {
    if (offerData?.Invoice?.Mtpl?.Request) {
      return {
        Insurer: offerData.Invoice.Mtpl.Request.Insurer,
        Period: offerData.Invoice.Mtpl.Request.Period,
        Payments: offerData.Invoice.Mtpl.Request.Payments,
      };
    }
    return null;
  }, [offerData]);

  // Extract requested insurers and periods from the offer request for display purposes
  // This ensures we show ALL originally requested insurers/periods, even those without data
  const requestedMtplInsurers = useMemo(() => {
    // First try to get from offer request data (saved offer)
    const fromRequest = offerData?.Offer?.Request?.Mtpl?.SelectedInsurers;
    if (fromRequest && Array.isArray(fromRequest) && fromRequest.length > 0) {
      return fromRequest;
    }
    // Fall back to currently selected insurers from form
    return selectedInsurers;
  }, [offerData, selectedInsurers]);

  const requestedMtplPeriods = useMemo(() => {
    // First try to get from offer request data (saved offer)
    const fromRequest =
      offerData?.Offer?.Request?.Mtpl?.SelectedMonthsOrPayments;
    if (fromRequest && Array.isArray(fromRequest) && fromRequest.length > 0) {
      return fromRequest;
    }
    // Fall back to currently selected periods from form (convert string IDs to month numbers)
    return selectedPeriods
      .map((periodId: string) => {
        const period = QUOTE_PERIODS.find((p) => p.id === periodId);
        return period ? period.months : parseInt(periodId, 10);
      })
      .filter((p: number) => !isNaN(p));
  }, [offerData, selectedPeriods]);

  // Memoized callback for quote selection to prevent infinite loops
  const handleQuoteSelectionChange = useCallback(
    (
      selection: {
        insurer: string;
        premium: number;
        payments: number;
        period?: number;
        insurerDiscount?: number;
        commissionDiscount?: number;
      } | null,
    ) => {
      setSelectedQuoteForInvoice(selection);
    },
    [],
  );

  // Notify parent when offerId changes (for backward compatibility)
  useEffect(() => {
    if (onOfferIdChange) {
      onOfferIdChange(offerId);
    }
  }, [offerId, onOfferIdChange]);

  // Reset hasLoadedFromOffer when offerData.OfferId changes (navigation to different offer)
  // This ensures documents and quotes load correctly after navigation
  useEffect(() => {
    if (offerData?.OfferId && offerData.OfferId !== offerId) {
      console.log(
        "OfferId mismatch detected - resetting hasLoadedFromOffer flag",
        { offerDataId: offerData.OfferId, currentOfferId: offerId },
      );
      setHasLoadedFromOffer(false);
    }
  }, [offerData?.OfferId, offerId]);

  // Initialize selections from form on mount
  useEffect(() => {
    const currentInsurers = getValues("mtplSelections.selectedInsurers");
    const currentPeriods = getValues("mtplSelections.selectedPeriods");

    console.log("MTPL INIT - currentInsurers:", currentInsurers);
    console.log("MTPL INIT - currentPeriods:", currentPeriods);

    // Initialize with defaults if not set - select all insurers and all periods
    if (!currentInsurers || currentInsurers.length === 0) {
      const allInsurers = MTPL_INSURERS.filter(
        (i) => i.id !== "Gjensidige",
      ).map((i) => i.id);
      console.log("MTPL INIT - Setting default insurers to all:", allInsurers);
      setValue("mtplSelections.selectedInsurers", allInsurers);
    }
    if (!currentPeriods || currentPeriods.length === 0) {
      const allPeriods = QUOTE_PERIODS.map((p) => p.id);
      console.log("MTPL INIT - Setting default periods to all:", allPeriods);
      setValue("mtplSelections.selectedPeriods", allPeriods);
    }
  }, [getValues, setValue]);

  // Create offer mutation (replaces calculation mutation)
  const createOfferMutation = useCreateOfferMutation((response) => {
    console.log("Create offer response:", response);

    // Extract OfferId from response (PascalCase in actual API)
    if (response.OfferId) {
      console.log("Got OfferId from response:", response.OfferId);
      setValue("proposalData.offerId", response.OfferId); // Store in form state
      setValue("proposalData.proposalNumber", response.OfferId); // Also set proposal number for display

      // Navigate to the offer URL while staying on the proposal tab
      navigate(`/offer/${response.OfferId}`, { replace: true });
    }

    // Update PDF/DOCX paths if available (nested under Offer with PascalCase)
    // These are the initial offer documents from Get Quotes operation
    if (response.Offer?.PathToOfferPdf) {
      console.log("Got offer PDF path:", response.Offer.PathToOfferPdf);
      setValue("mtplDocuments.offerPdfPath", response.Offer.PathToOfferPdf);
    }
    if (response.Offer?.PathToOfferDocx) {
      console.log("Got offer DOCX path:", response.Offer.PathToOfferDocx);
      setValue("mtplDocuments.offerDocxPath", response.Offer.PathToOfferDocx);
    }

    // The API returns: response.Offer.Response.Mtpl (PascalCase)
    if (response.Offer?.Response?.Mtpl) {
      const mtplData = response.Offer.Response.Mtpl;
      console.log("Got MTPL data from Offer.Response:", mtplData);

      if (mtplData.length > 0) {
        // Store raw API data for MtplQuotesDisplay component
        setRawMtplData(mtplData);
        // Don't auto-select when user clicks Get Quotes - preserve their selection
        mapMtplResponseToQuotes(mtplData, false);
        // Mark that we've loaded fresh data, so we don't override it from offerData
        setHasLoadedFromOffer(true);
        // This is fresh calculation data, not saved
        setIsSavedData(false);
      } else {
        console.log("No MTPL quotes returned - empty array");
        setRawMtplData([]);
        setQuotes([]);
        setIsSavedData(false);
      }
    } else {
      console.warn("No MTPL data in response. Response structure:", response);
    }
  });

  // Calculation mutation (does not create offer)
  const calculationMutation = useCalculationMutation((response) => {
    console.log("Calculation completed:", response);
    setIsCalculating(false);

    // The API returns: response.Calculation.Response.Mtpl (PascalCase)
    if (response.Calculation?.Response?.Mtpl) {
      const mtplData = response.Calculation.Response.Mtpl;
      console.log("Got MTPL data from Calculation.Response:", mtplData);

      if (mtplData.length > 0) {
        // Store raw API data for MtplQuotesDisplay component
        setRawMtplData(mtplData);
        // Don't auto-select when user clicks Calculate - preserve their selection
        mapMtplResponseToQuotes(mtplData, false);
        // This is calculation data, not saved
        setIsSavedData(false);
      } else {
        console.log("No MTPL quotes returned - empty array");
        setRawMtplData([]);
        setQuotes([]);
        setIsSavedData(false);
      }
    } else {
      console.warn("No MTPL data in response. Response structure:", response);
    }
  });

  // Reset calculating state on mutation error
  useEffect(() => {
    if (calculationMutation.isError) {
      setIsCalculating(false);
    }
  }, [calculationMutation.isError]);

  // Notify parent component when calculation state changes
  useEffect(() => {
    const calculating =
      createOfferMutation.isPending ||
      calculationMutation.isPending ||
      isCalculating;
    if (onCalculationStateChange) {
      onCalculationStateChange(calculating);
    }
  }, [
    createOfferMutation.isPending,
    calculationMutation.isPending,
    isCalculating,
    onCalculationStateChange,
  ]);

  // Helper function to map MTPL response to quotes state
  const mapMtplResponseToQuotes = useCallback(
    (mtplData: MtplQuoteData[], autoSelectInsurers: boolean = false) => {
      console.log("=== Mapping MTPL Data ===");
      console.log("Raw MTPL data:", mtplData);
      console.log("Auto-select insurers:", autoSelectInsurers);
      console.log(
        "Available insurers in MTPL_INSURERS:",
        MTPL_INSURERS.map((i) => i.id),
      );

      // Group data by insurer first to handle multiple entries for same insurer
      const groupedByInsurer: { [insurer: string]: MtplQuoteData[] } = {};

      mtplData.forEach((quote) => {
        const insurerKey = quote.Insurer.toLowerCase();
        if (!groupedByInsurer[insurerKey]) {
          groupedByInsurer[insurerKey] = [];
        }
        groupedByInsurer[insurerKey].push(quote);
      });

      console.log("Grouped by insurer:", groupedByInsurer);

      const loadedQuotes: MtplQuote[] = Object.entries(groupedByInsurer)
        .map(([insurerKey, quotes]) => {
          console.log(`Processing quotes for "${insurerKey}":`, quotes);

          const premiums: { [period: number]: number } = {};
          let bonusMalus: string | undefined;
          let hasError = false;
          let hasPrices = false;
          const errorMessages: string[] = [];
          const errorList: Array<{ message: string; code: number }> = [];
          const exceptionList: Array<{
            genericMessage: string;
            code: number;
            genericStackTrace?: string;
          }> = [];

          // Merge all quotes for this insurer
          quotes.forEach((quote) => {
            // Map premiums by period
            if (quote.Premiums && Array.isArray(quote.Premiums)) {
              console.log(
                `  - Found ${quote.Premiums.length} premiums in quote`,
              );
              quote.Premiums.forEach((premium) => {
                console.log(
                  `    Period ${premium.Period}: ${premium.ClientPremium}€`,
                );
                premiums[premium.Period] = premium.ClientPremium;
              });
            }

            // Keep bonus malus if found
            if (quote.BonusMalus) {
              bonusMalus = quote.BonusMalus;
            }

            // Track errors and prices
            if (quote.HasErrors) {
              hasError = true;
            }
            if (quote.HasPrices) {
              hasPrices = true;
            }

            // Collect error messages from both ErrorList and ExceptionList
            if (quote.ErrorList && quote.ErrorList.length > 0) {
              errorMessages.push(...quote.ErrorList.map((e) => e.Message));
              // Store full error objects with codes
              errorList.push(
                ...quote.ErrorList.map((e) => ({
                  message: e.Message,
                  code: e.Code,
                })),
              );
            }
            if (quote.ExceptionList && quote.ExceptionList.length > 0) {
              errorMessages.push(
                ...quote.ExceptionList.map((e) => e.GenericMessage),
              );
              // Store full exception objects with codes and stack traces
              exceptionList.push(
                ...quote.ExceptionList.map((e) => ({
                  genericMessage: e.GenericMessage,
                  code: e.Code,
                  genericStackTrace: e.GenericStackTrace,
                })),
              );
            }
          });

          // Normalize insurer name to match our MTPL_INSURERS array
          const firstQuote = quotes[0];
          if (!firstQuote) {
            console.warn(`  - WARNING: Empty quotes array for "${insurerKey}"`);
            return null;
          }

          let normalizedInsurer = firstQuote.Insurer;
          const matchingInsurer = MTPL_INSURERS.find(
            (i) => i.id.toLowerCase() === insurerKey,
          );
          if (matchingInsurer) {
            normalizedInsurer = matchingInsurer.id;
            console.log(
              `  - Normalized insurer from "${firstQuote.Insurer}" to "${normalizedInsurer}"`,
            );
          } else {
            console.warn(
              `  - WARNING: No matching insurer found for "${firstQuote.Insurer}"`,
            );
          }

          const mappedQuote: MtplQuote = {
            insurer: normalizedInsurer,
            bonusMalus: bonusMalus,
            premiums,
            hasError: hasError,
            hasPrices: hasPrices && Object.keys(premiums).length > 0,
            errorMessage:
              errorMessages.length > 0
                ? Array.from(new Set(errorMessages)).join("; ") // Remove duplicates
                : undefined,
            // Include detailed error and exception information (same as CASCO)
            errorList: errorList.length > 0 ? errorList : undefined,
            exceptionList: exceptionList.length > 0 ? exceptionList : undefined,
          };

          console.log(`  - Mapped quote:`, mappedQuote);
          return mappedQuote;
        })
        .filter((q): q is MtplQuote => q !== null) as MtplQuote[];

      console.log("=== Final mapped quotes ===", loadedQuotes);
      setQuotes(loadedQuotes);

      // Auto-select insurers only when loading existing proposal (not after Get Quotes button)
      // Show quotes even if they have errors - error status should not hide available data
      if (autoSelectInsurers) {
        const insurersWithQuotes = loadedQuotes
          .filter((q) => Object.keys(q.premiums).length > 0) // Only filter based on data availability
          .map((q) => q.insurer);
        console.log("MTPL - Auto-selecting insurers:", insurersWithQuotes);
        setValue("mtplSelections.selectedInsurers", insurersWithQuotes);
      }
    },
    [setValue, setQuotes],
  );

  // Handler for clicking invoice PDF link - refetches offer data to update payment status
  const handleInvoicePdfClick = () => {
    if (offerId) {
      // Force refetch to get updated payment status immediately
      queryClient.refetchQueries({ queryKey: [QUERY_KEYS.offer, offerId] });
    }
  };

  // Handler for Calculate button (calls Calculations API)
  const handleCalculate = () => {
    // Clear previous validation errors
    clearErrors();
    setIsCalculating(true);

    const formData = getValues();
    console.log("Form data:", formData);
    console.log("Selected insurers:", selectedInsurers);
    console.log("Selected periods:", selectedPeriods);

    // Convert selected period IDs to months (numbers)
    const selectedMonths = selectedPeriods.map((periodId) => {
      const period = QUOTE_PERIODS.find((p) => p.id === periodId);
      return period ? period.months : 12;
    });
    console.log("Selected months:", selectedMonths);

    // Transform form data to API request format
    const requestBody = transformProposalToCalculationRequest(
      formData,
      taxonomy as any,
      selectedInsurers.map((insurer) => insurer.toLowerCase()),
      ["mtpl"],
      selectedMonths,
      userInfo,
    );
    console.log("API request body:", requestBody);

    // Validate the request before sending to API
    const validation = validateRequest(requestBody);

    if (!validation.isValid) {
      const errorCount = validation.errors.filter(
        (e) => e.severity === "error",
      ).length;
      toast.error(
        `Please correct ${errorCount} validation error${
          errorCount !== 1 ? "s" : ""
        } before calculating`,
        { autoClose: 2000 },
      );
      setIsCalculating(false);
      return;
    }

    // Call the Calculations API (does not create offer)
    calculationMutation.mutate(requestBody);
  };

  // Handler for Get Quotes button
  const handleGetQuotes = () => {
    // Clear previous validation errors
    clearErrors();

    const formData = getValues();
    console.log("Form data:", formData);
    console.log("Selected insurers:", selectedInsurers);
    console.log("Selected periods:", selectedPeriods);

    // Convert selected period IDs to months (numbers)
    const selectedMonths = selectedPeriods.map((periodId) => {
      const period = QUOTE_PERIODS.find((p) => p.id === periodId);
      return period ? period.months : 12; // Default to 12 if not found
    });
    console.log("Selected months:", selectedMonths);

    // Transform form data to API request format with selected insurers (lowercase) and months
    const requestBody = transformProposalToCalculationRequest(
      formData,
      taxonomy as any,
      selectedInsurers.map((insurer) => insurer.toLowerCase()),
      ["mtpl"], // Explicitly request MTPL quotes
      selectedMonths, // Pass selected months
      userInfo,
    );
    console.log("API request body:", requestBody);

    // Validate the request before sending to API
    const validation = validateRequest(requestBody);

    if (!validation.isValid) {
      const errorCount = validation.errors.filter(
        (e) => e.severity === "error",
      ).length;
      toast.error(
        `Please correct ${errorCount} validation error${
          errorCount !== 1 ? "s" : ""
        } before calculating`,
        { autoClose: 2000 }, // Reduced from 5000 to 2000ms since validation errors are displayed inline
      );
      // Don't call scrollToFirstError here - the error fields are in General Info tab
      // User can see the errors when they switch tabs
      return;
    }

    // Call the Offers API (replaces Calculations API)
    createOfferMutation.mutate(requestBody);
  };

  // Save Offer mutation
  const saveOfferMutation = useSaveOfferMutation((data) => {
    console.log("Save offer response:", data);
    console.log("Save offer status:", data?.OfferStatus);

    // Update saved PDF/DOCX paths if available
    if (data?.Offer?.PathToSavedOfferPdf) {
      console.log("Got saved offer PDF path:", data.Offer.PathToSavedOfferPdf);
      setValue("mtplDocuments.savedPdfPath", data.Offer.PathToSavedOfferPdf);
      // Clear offer PDF path since we now have saved version
      setValue("mtplDocuments.offerPdfPath", null);
    }
    if (data?.Offer?.PathToSavedOfferDocx) {
      console.log(
        "Got saved offer DOCX path:",
        data.Offer.PathToSavedOfferDocx,
      );
      setValue("mtplDocuments.savedDocxPath", data.Offer.PathToSavedOfferDocx);
      // Clear offer DOCX path since we now have saved version
      setValue("mtplDocuments.offerDocxPath", null);
    }

    // Handle MtplSaved data from save response
    // The API returns MtplSaved array after saving, which should replace the current quotes
    if (
      data?.Offer?.Response?.MtplSaved &&
      data.Offer.Response.MtplSaved.length > 0
    ) {
      const mtplSavedData = data.Offer.Response.MtplSaved;
      console.log("Got MtplSaved data from save response:", mtplSavedData);

      // IMPORTANT: Merge both Mtpl and MtplSaved data
      // MtplSaved represents save attempts and contains additional errors
      // The original Mtpl data is needed to display premiums
      const originalMtpl = data?.Offer?.Response?.Mtpl || rawMtplData;

      console.log("Using original Mtpl data for display:", originalMtpl);
      console.log("MtplSaved contains save attempt results:", mtplSavedData);

      // Merge the original calculation data with save attempt results
      // This ensures all errors from both sources are visible
      // Mark items with their source so we can separate them later
      const combinedData = originalMtpl.map((item: any) => ({
        ...item,
        _source: "create",
      }));
      if (mtplSavedData && mtplSavedData.length > 0) {
        const savedWithSource = mtplSavedData.map((item: any) => ({
          ...item,
          _source: "save",
        }));
        combinedData.push(...savedWithSource);
      }

      if (combinedData.length > 0) {
        setRawMtplData(combinedData);
        mapMtplResponseToQuotes(originalMtpl, false);
      }

      // Mark as saved data
      setIsSavedData(true);

      // Clear selectedQuoteForInvoice if no valid premiums exist
      const hasValidPremiums = mtplSavedData.some(
        (quote: any) =>
          quote.Premiums &&
          Array.isArray(quote.Premiums) &&
          quote.Premiums.length > 0 &&
          quote.HasPrices,
      );
      if (!hasValidPremiums) {
        console.log(
          "No valid premiums found after save - clearing selectedQuoteForInvoice",
        );
        setSelectedQuoteForInvoice(null);
      }
    }

    // Preserve MtplUpdated data from save response
    // Updates should persist after save so they remain visible
    if (data?.Offer?.Response?.MtplUpdated) {
      const updatedData = data.Offer.Response.MtplUpdated;
      console.log(
        "🔄 SAVE CALLBACK: Got MtplUpdated from save response:",
        updatedData,
      );
      console.log(
        "🔄 SAVE CALLBACK: Setting mtplUpdated state to:",
        updatedData,
      );
      setMtplUpdated(updatedData);
      setMtplSavedUpdates(updatedData);
    } else {
      console.log("⚠️ SAVE CALLBACK: No MtplUpdated in save response");
    }

    // Notify parent component about the updated offer data
    // This ensures the offer status and other metadata are refreshed in the UI
    if (onOfferDataUpdate && data) {
      onOfferDataUpdate(data);
    }
  });

  // Update Offer mutation - sends modified quote values to backend
  const updateOfferMutation = useUpdateOfferMutation(
    offerId || "",
    (response) => {
      console.log("Update offer response:", response);

      // Update PDF/DOCX paths if regenerated
      if (response.Offer?.PathToOfferPdf) {
        console.log(
          "Got updated offer PDF path:",
          response.Offer.PathToOfferPdf,
        );
        setValue("mtplDocuments.offerPdfPath", response.Offer.PathToOfferPdf);
      }
      if (response.Offer?.PathToOfferDocx) {
        console.log(
          "Got updated offer DOCX path:",
          response.Offer.PathToOfferDocx,
        );
        setValue("mtplDocuments.offerDocxPath", response.Offer.PathToOfferDocx);
      }

      // Update quotes with MtplUpdated data from backend
      if (response.Offer?.Response?.MtplUpdated) {
        const updatedData = response.Offer.Response.MtplUpdated;
        console.log("Got MtplUpdated from backend:", updatedData);
        // Store in local state to display updated values
        setMtplUpdated(updatedData);
        // Mark these as saved (so we know there are no NEW pending changes)
        setMtplSavedUpdates(updatedData);
      }

      // Update exclusions with MtplExcluded data from backend
      if (response.Offer?.Response?.MtplExcluded) {
        const excludedData = response.Offer.Response.MtplExcluded;
        console.log("Got MtplExcluded from backend:", excludedData);
        setMtplExcluded(excludedData);
        setMtplSavedExclusions(excludedData);
      }

      // Refresh quotes with latest Mtpl data if available
      if (response.Offer?.Response?.Mtpl) {
        const mtplData = response.Offer.Response.Mtpl;
        const mtplSavedData = response.Offer?.Response?.MtplSaved || [];

        // Merge Mtpl and MtplSaved arrays to show all errors
        // Mark items with their source so we can separate them later
        const combinedData = mtplData.map((item: any) => ({
          ...item,
          _source: "create",
        }));
        if (mtplSavedData.length > 0) {
          const savedWithSource = mtplSavedData.map((item: any) => ({
            ...item,
            _source: "save",
          }));
          combinedData.push(...savedWithSource);
        }

        setRawMtplData(combinedData);
        mapMtplResponseToQuotes(mtplData, false);
      }

      // Don't clear mtplUpdated - keep all updates, but they're now saved

      // Notify parent component about the updated offer data
      if (onOfferDataUpdate && response) {
        onOfferDataUpdate(response);
      }
    },
  );
  console.log(
    createOfferMutation.isPending ||
      calculationMutation.isPending ||
      isCalculating ||
      saveOfferMutation.isPending ||
      (offerData?.Offer?.Response?.MtplSaved &&
        offerData.Offer.Response.MtplSaved.some((s) => s.PolicyNumber)) ||
      (offerData?.Offer?.Response?.CascoSaved &&
        offerData.Offer.Response.CascoSaved.some((s) => s.PolicyNumber)),
  );
  // Handler: Add or update a quote in the pending updates list
  const handleQuoteUpdate = useCallback(
    (update: QuoteUpdateDto, originalPremium: number | null) => {
      setMtplUpdated((prev) => {
        // Remove existing update for same insurer/period/payments/variantID
        const filtered = prev.filter(
          (q) =>
            !(
              q.Insurer.toLowerCase() === update.Insurer.toLowerCase() &&
              q.Period === update.Period &&
              q.Payments === update.Payments &&
              q.VariantID === update.VariantID
            ),
        );

        // If premium matches original AND is not null, this is a "remove override" - don't add it back
        // But if premium is null, always add it (user explicitly wants to mark as "not offered")
        if (update.Premium !== null && update.Premium === originalPremium) {
          return filtered;
        }

        // Add new update
        return [...filtered, update];
      });
    },
    [],
  );

  // Handle exclusion change from MtplQuotesDisplay
  const handleExclusionChange = useCallback((exclusions: ExcludedItemDto[]) => {
    console.log("📋 MTPL Exclusion change:", exclusions);
    setMtplExcluded(exclusions);
  }, []);

  // Check if there are NEW pending changes (not yet saved)
  const hasNewPendingChanges = useMemo(() => {
    // Check quote updates
    if (mtplUpdated.length !== mtplSavedUpdates.length) return true;

    // Check if any update is different from saved
    const hasQuoteChanges = mtplUpdated.some((update) => {
      const saved = mtplSavedUpdates.find(
        (s) =>
          s.Insurer === update.Insurer &&
          s.Period === update.Period &&
          s.Payments === update.Payments &&
          s.VariantID === update.VariantID,
      );
      return !saved || saved.Premium !== update.Premium;
    });

    if (hasQuoteChanges) return true;

    // Check exclusion changes
    if (mtplExcluded.length !== mtplSavedExclusions.length) return true;

    const hasExclusionChanges = mtplExcluded.some((exclusion) => {
      const saved = mtplSavedExclusions.find(
        (s) => s.Insurer === exclusion.Insurer,
      );
      return !saved;
    });

    return hasExclusionChanges;
  }, [mtplUpdated, mtplSavedUpdates, mtplExcluded, mtplSavedExclusions]);

  // Handler: Send all pending updates to backend
  const handleUpdateOffer = useCallback(() => {
    if (!offerId) return;

    console.log("Sending MTPL updates to backend:", mtplUpdated);
    console.log("Sending MTPL exclusions to backend:", mtplExcluded);
    updateOfferMutation.mutate({
      MtplUpdated: mtplUpdated,
      MtplExcluded: mtplExcluded,
    });
  }, [offerId, mtplUpdated, mtplExcluded, updateOfferMutation]);

  // Handler for Save Offer button
  const handleSaveOffer = () => {
    if (!offerId) {
      toast.error(
        t(
          "qtg.proposal.mtplQuotes.noOfferId",
          "No offer ID available. Please get quotes first.",
        ),
      );
      return;
    }

    saveOfferMutation.mutate(offerId);
  };

  // Load quotes from offerData when component mounts or offerData changes
  useEffect(() => {
    console.log("=== MtplQuotesTab useEffect - offerData changed ===", {
      hasOfferData: !!offerData,
      offerId: offerData?.OfferId,
      hasPdfPath: !!offerData?.Offer?.PathToOfferPdf,
      hasDocxPath: !!offerData?.Offer?.PathToOfferDocx,
      currentFormOfferId: offerId,
      currentOfferPdfPath: offerPdfPath,
      currentOfferDocxPath: offerDocxPath,
    });

    // Always extract OfferId and document paths when offerData is available, regardless of quotes
    if (offerData?.OfferId) {
      console.log("Loading OfferId from offerData:", offerData.OfferId);
      setValue("proposalData.offerId", offerData.OfferId); // Store in form state
    }

    // Extract document paths - prioritize saved documents over offer documents
    // IMPORTANT: Only update document paths if they exist in offerData
    // Don't clear existing paths if offerData doesn't have them (prevents clearing after navigation)
    if (offerData?.Offer?.PathToSavedOfferPdf) {
      console.log(
        "Loading saved offer PDF path from offerData:",
        offerData.Offer.PathToSavedOfferPdf,
      );
      setValue(
        "mtplDocuments.savedPdfPath",
        offerData.Offer.PathToSavedOfferPdf,
      );
    } else if (offerData?.Offer?.PathToOfferPdf) {
      console.log(
        "Loading offer PDF path from offerData:",
        offerData.Offer.PathToOfferPdf,
      );
      setValue("mtplDocuments.offerPdfPath", offerData.Offer.PathToOfferPdf);
    }

    if (offerData?.Offer?.PathToSavedOfferDocx) {
      console.log(
        "Loading saved offer DOCX path from offerData:",
        offerData.Offer.PathToSavedOfferDocx,
      );
      setValue(
        "mtplDocuments.savedDocxPath",
        offerData.Offer.PathToSavedOfferDocx,
      );
    } else if (offerData?.Offer?.PathToOfferDocx) {
      console.log(
        "Loading offer DOCX path from offerData:",
        offerData.Offer.PathToOfferDocx,
      );
      setValue("mtplDocuments.offerDocxPath", offerData.Offer.PathToOfferDocx);
    }

    // Extract invoice documents if available
    if (offerData?.Invoice?.Mtpl?.PathToInvoicePdf) {
      console.log(
        "Loading invoice PDF path from offerData:",
        offerData.Invoice.Mtpl.PathToInvoicePdf,
      );
      setValue(
        "mtplDocuments.invoicePdfPath",
        offerData.Invoice.Mtpl.PathToInvoicePdf,
      );
    }

    if (offerData?.Invoice?.Mtpl?.PathToInvoiceDocx) {
      console.log(
        "Loading invoice DOCX path from offerData:",
        offerData.Invoice.Mtpl.PathToInvoiceDocx,
      );
      setValue(
        "mtplDocuments.invoiceDocxPath",
        offerData.Invoice.Mtpl.PathToInvoiceDocx,
      );
    }

    // Load MTPL updates from offerData if available
    // IMPORTANT: This must be BEFORE early return guards so updates are always reloaded
    if (offerData?.Offer?.Response?.MtplUpdated) {
      const updatedData = offerData.Offer.Response.MtplUpdated;
      console.log(
        "📥 USEEFFECT: Loading MTPL updates from offerData:",
        updatedData,
      );
      console.log("📥 USEEFFECT: Setting mtplUpdated state to:", updatedData);
      setMtplUpdated(updatedData);
      // Also set as saved updates since they came from the backend
      setMtplSavedUpdates(updatedData);
    } else {
      console.log("⚠️ USEEFFECT: No MtplUpdated in offerData");
    }

    // Load MTPL exclusions from offerData if available
    if (offerData?.Offer?.Response?.MtplExcluded) {
      const excludedData = offerData.Offer.Response.MtplExcluded;
      console.log(
        "📥 USEEFFECT: Loading MTPL exclusions from offerData:",
        excludedData,
      );
      setMtplExcluded(excludedData);
      setMtplSavedExclusions(excludedData);
    }

    // Skip loading quotes if we've already loaded fresh offer data
    if (hasLoadedFromOffer) {
      console.log("Skipping quotes load - already have fresh offer data");
      return;
    }

    // Skip loading quotes if quotes already exist (preserve calculated quotes)
    if (quotes.length > 0) {
      console.log("Skipping quotes load - quotes already exist");
      return;
    }

    // Skip loading quotes when duplicating an offer (isNewProposal flag indicates duplicate)
    if (isNewProposal && offerData?.Offer) {
      console.log("Skipping quotes load - duplicating offer");
      return;
    }

    // Get MTPL data from structure: Offer.Response.Mtpl
    // Business rule: ALWAYS use Mtpl for quote data (contains variants with premiums)
    // MtplSaved should be merged with Mtpl for comprehensive error display
    const mtplData = offerData?.Offer?.Response?.Mtpl;
    const mtplSavedData = offerData?.Offer?.Response?.MtplSaved;

    const isLoadingSavedData = mtplSavedData && mtplSavedData.length > 0;

    if (mtplData && mtplData.length > 0) {
      console.log("Loading MTPL quotes from existing offer:", mtplData);

      // Merge Mtpl and MtplSaved arrays for comprehensive display (including save errors)
      // Mark items with their source so we can separate them later
      const combinedData = mtplData.map((item: any) => ({
        ...item,
        _source: "create",
      }));
      if (mtplSavedData && mtplSavedData.length > 0) {
        console.log("Adding MtplSaved data for error display:", mtplSavedData);
        const savedWithSource = mtplSavedData.map((item: any) => ({
          ...item,
          _source: "save",
        }));
        combinedData.push(...savedWithSource);
      }

      // Store combined raw API data for MtplQuotesDisplay component
      setRawMtplData(combinedData);
      // Auto-select insurers when loading existing proposal (use original Mtpl data for mapping)
      mapMtplResponseToQuotes(mtplData, true);
      setHasLoadedFromOffer(true);
      // Mark if this is saved data (but NOT for new proposals/duplicates)
      setIsSavedData(!!isLoadingSavedData && !isNewProposal);
    }
  }, [
    offerData,
    mapMtplResponseToQuotes,
    hasLoadedFromOffer,
    isNewProposal,
    setValue,
    quotes.length,
    setRawMtplData,
    offerId,
    offerPdfPath,
    offerDocxPath,
  ]);

  const handleInsurerToggle = (insurerId: string) => {
    const current = selectedInsurers;
    const updated = current.includes(insurerId)
      ? current.filter((id) => id !== insurerId)
      : [...current, insurerId];
    console.log(`MTPL - Setting selectedInsurers to:`, updated);
    setValue("mtplSelections.selectedInsurers", updated);
  };

  const handlePeriodToggle = (periodId: string) => {
    const current = selectedPeriods;
    const updated = current.includes(periodId)
      ? current.filter((id) => id !== periodId)
      : [...current, periodId];
    console.log(`MTPL - Setting selectedPeriods to:`, updated);
    setValue("mtplSelections.selectedPeriods", updated);
  };

  // Group rawMtplData by insurer for error counting
  const groupedErrorData = useMemo(() => {
    const grouped: { [insurer: string]: MtplQuoteData } = {};
    rawMtplData.forEach((quote) => {
      const insurerKey = quote.Insurer.toLowerCase();
      if (!grouped[insurerKey]) {
        grouped[insurerKey] = { ...quote };
      } else {
        // Merge error lists
        const existing = grouped[insurerKey];
        if (quote.ErrorList && quote.ErrorList.length > 0) {
          const existingErrors = existing.ErrorList || [];
          const errorMessages = new Set(existingErrors.map((e) => e.Message));
          quote.ErrorList.forEach((error) => {
            if (!errorMessages.has(error.Message)) {
              existingErrors.push(error);
            }
          });
          existing.ErrorList = existingErrors;
        }
        // Update HasErrors flag if any quote has errors
        if (quote.HasErrors) {
          existing.HasErrors = true;
        }
        // Update HasInsurerWarnings flag if any quote has warnings
        if (quote.HasInsurerWarnings) {
          existing.HasInsurerWarnings = true;
        }
        // Update HasInsurerErrors flag if any quote has insurer errors
        if (quote.HasInsurerErrors) {
          existing.HasInsurerErrors = true;
        }
      }
    });
    return Object.values(grouped);
  }, [rawMtplData]);

  // Merge errors from regular and saved quotes with source labels for error dialog
  const mergedErrorDataForDialog = useMemo(() => {
    const grouped: {
      [insurer: string]: {
        insurer: string;
        errorList: Array<{ code: number; message: string; source: string }>;
        exceptionList: Array<{
          code: number;
          genericMessage: string;
          genericStackTrace?: string;
          source: string;
        }>;
        hasErrors: boolean;
        bonusMalus?: string;
      };
    } = {};

    rawMtplData.forEach((quote) => {
      const insurerKey = quote.Insurer.toLowerCase();
      // Determine if this is a saved quote (any attempt with HasOffer flag)
      const isSaved = quote.HasOffer === true;
      const source = isSaved ? "Saved" : "Quote";

      if (!grouped[insurerKey]) {
        grouped[insurerKey] = {
          insurer: quote.Insurer,
          errorList: [],
          exceptionList: [],
          hasErrors: false,
          bonusMalus: quote.BonusMalus || undefined,
        };
      }

      // Merge error lists with source labels
      if (quote.ErrorList && quote.ErrorList.length > 0) {
        const errorMessages = new Set(
          grouped[insurerKey]!.errorList.map((e) => `${e.source}:${e.message}`),
        );
        quote.ErrorList.forEach((error) => {
          const errorKey = `${source}:${error.Message}`;
          if (!errorMessages.has(errorKey)) {
            grouped[insurerKey]!.errorList.push({
              code: error.Code,
              message: error.Message,
              source: source,
            });
            errorMessages.add(errorKey);
          }
        });
      }

      // Update hasErrors flag if any quote has errors
      if (quote.HasErrors) {
        grouped[insurerKey]!.hasErrors = true;
      }

      // Keep BonusMalus if found and not already set
      if (quote.BonusMalus && !grouped[insurerKey]!.bonusMalus) {
        grouped[insurerKey]!.bonusMalus = quote.BonusMalus;
      }
    });

    return Object.values(grouped);
  }, [rawMtplData]);

  // Separate create errors and save errors for display
  const errorCounts = useMemo(() => {
    let createErrors = 0;
    let saveErrors = 0;

    rawMtplData.forEach((quote) => {
      const isSaved = quote.HasOffer === true;
      const hasErrors = quote.HasErrors || quote.HasInsurerErrors;

      if (hasErrors) {
        if (isSaved) {
          saveErrors++;
        } else {
          createErrors++;
        }
      }
    });

    return { createErrors, saveErrors };
  }, [rawMtplData]);

  // Separate create and save quotes for MtplQuotesDisplay
  // Use _source marker to identify items from Response.Mtpl vs Response.MtplSaved
  const { createQuotes, saveQuotes } = useMemo(() => {
    const create: MtplQuoteData[] = [];
    const save: MtplQuoteData[] = [];

    rawMtplData.forEach((quote: any) => {
      if (quote._source === "save") {
        save.push(quote);
      } else {
        create.push(quote);
      }
    });

    return { createQuotes: create, saveQuotes: save };
  }, [rawMtplData]);

  return (
    <TabWrapper>
      <ScrollableContent>
        {/* Proposal Documents Section */}
        {(quotes.length > 0 ||
          savedPdfPath ||
          savedDocxPath ||
          offerPdfPath ||
          offerDocxPath ||
          invoicePdfPath ||
          invoiceDocxPath) && (
          <DocumentsSection data-help="offer-documents">
            <DocumentsTitle>
              {t("qtg.proposal.documents.title", "Proposal Documents")}
            </DocumentsTitle>
            {savedPdfPath ||
            savedDocxPath ||
            offerPdfPath ||
            offerDocxPath ||
            invoicePdfPath ||
            invoiceDocxPath ? (
              <DocumentsGrid>
                {/* Show saved PDF if available, otherwise show offer PDF */}
                {(savedPdfPath || offerPdfPath) && (
                  <FileInfoBox>
                    <FileInfoHeader>
                      <DocumentLink
                        href={savedPdfPath || offerPdfPath || "#"}
                        target="_blank"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          style={{ marginRight: "8px", flexShrink: 0 }}
                        >
                          <path d="M4 4C4 2.89543 4.89543 2 6 2H10.5858C11.1162 2 11.6249 2.21071 12 2.58579L15.4142 6C15.7893 6.37507 16 6.88378 16 7.41421V16C16 17.1046 15.1046 18 14 18H6C4.89543 18 4 17.1046 4 16V4Z" />
                          <path
                            d="M11 2.5V6C11 6.55228 11.4477 7 12 7H15.5"
                            fill="white"
                          />
                          <text
                            x="10"
                            y="14"
                            fontSize="6"
                            fill="white"
                            textAnchor="middle"
                            fontWeight="bold"
                          >
                            PDF
                          </text>
                        </svg>
                        {savedPdfPath
                          ? t(
                              "qtg.proposal.documents.savedOfferPdf",
                              "Saved Offer (PDF)",
                            )
                          : t("qtg.proposal.documents.offerPdf", "Offer (PDF)")}
                      </DocumentLink>
                      {/* Info icon with last modified details */}
                      {offerData?.Offer?.OfferLastModifiedAt && (
                        <Tooltip
                          content={
                            <div>
                              {offerData.Offer
                                .OfferLastModifiedByUserFullName ||
                              offerData.Offer.OfferLastModifiedByUsername
                                ? t(
                                    "qtg.proposal.lifecycle.lastModified",
                                    "Last modified by {{userFullName}} ({{username}}) on {{date}}",
                                    {
                                      userFullName:
                                        offerData.Offer
                                          .OfferLastModifiedByUserFullName ||
                                        "",
                                      username:
                                        offerData.Offer
                                          .OfferLastModifiedByUsername || "",
                                      date: format(
                                        new Date(
                                          offerData.Offer.OfferLastModifiedAt,
                                        ),
                                        "dd.MM.yyyy HH:mm",
                                      ),
                                    },
                                  )
                                : t(
                                    "qtg.proposal.lifecycle.lastModifiedNoUser",
                                    "Last modified on {{date}}",
                                    {
                                      date: format(
                                        new Date(
                                          offerData.Offer.OfferLastModifiedAt,
                                        ),
                                        "dd.MM.yyyy HH:mm",
                                      ),
                                    },
                                  )}
                            </div>
                          }
                          relationship="description"
                        >
                          <Button
                            appearance="transparent"
                            size="small"
                            icon={<InfoRegular />}
                            style={{ minWidth: "auto", padding: "0 4px" }}
                          />
                        </Tooltip>
                      )}
                      {/* Hide send offer email button when invoice exists */}
                      {!invoicePdfPath && (
                        <EmailButtonContainer>
                          <Tooltip
                            content={t(
                              "qtg.proposal.buttons.sendEmail.tooltip",
                              "Send offer via email",
                            )}
                            relationship="label"
                          >
                            <Button
                              data-help="offer-send-offer-email"
                              appearance="subtle"
                              size="medium"
                              icon={<MailArrowUpRegular />}
                              onClick={() => setIsEmailDialogOpen(true)}
                              aria-label={t(
                                "qtg.proposal.documents.sendEmail",
                                "Send Email",
                              )}
                            />
                          </Tooltip>
                        </EmailButtonContainer>
                      )}
                    </FileInfoHeader>
                  </FileInfoBox>
                )}
                {/* Show saved DOCX if available, otherwise show offer DOCX */}
                {(savedDocxPath || offerDocxPath) && (
                  <FileInfoBox>
                    <DocumentLink
                      href={savedDocxPath || offerDocxPath || "#"}
                      target="_blank"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="#2B579A"
                        style={{ marginRight: "8px", flexShrink: 0 }}
                      >
                        <path d="M4 4C4 2.89543 4.89543 2 6 2H10.5858C11.1162 2 11.6249 2.21071 12 2.58579L15.4142 6C15.7893 6.37507 16 6.88378 16 7.41421V16C16 17.1046 15.1046 18 14 18H6C4.89543 18 4 17.1046 4 16V4Z" />
                        <path
                          d="M11 2.5V6C11 6.55228 11.4477 7 12 7H15.5"
                          fill="white"
                        />
                        <text
                          x="10"
                          y="14"
                          fontSize="5"
                          fill="white"
                          textAnchor="middle"
                          fontWeight="bold"
                        >
                          DOCX
                        </text>
                      </svg>
                      {savedDocxPath
                        ? t(
                            "qtg.proposal.documents.savedOfferDocx",
                            "Saved Offer (DOCX)",
                          )
                        : t("qtg.proposal.documents.offerDocx", "Offer (DOCX)")}
                    </DocumentLink>
                  </FileInfoBox>
                )}
                {/* Invoice PDF Document */}
                {invoicePdfPath && (
                  <FileInfoBox>
                    <FileInfoHeader>
                      <DocumentLink
                        href={invoicePdfPath}
                        target="_blank"
                        onClick={handleInvoicePdfClick}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          style={{ marginRight: "8px", flexShrink: 0 }}
                        >
                          <path d="M4 4C4 2.89543 4.89543 2 6 2H10.5858C11.1162 2 11.6249 2.21071 12 2.58579L15.4142 6C15.7893 6.37507 16 6.88378 16 7.41421V16C16 17.1046 15.1046 18 14 18H6C4.89543 18 4 17.1046 4 16V4Z" />
                          <path
                            d="M11 2.5V6C11 6.55228 11.4477 7 12 7H15.5"
                            fill="white"
                          />
                          <text
                            x="10"
                            y="14"
                            fontSize="6"
                            fill="white"
                            textAnchor="middle"
                            fontWeight="bold"
                          >
                            PDF
                          </text>
                        </svg>
                        {t(
                          "qtg.proposal.documents.invoicePdf",
                          "Invoice (PDF)",
                        )}
                      </DocumentLink>
                      <EmailButtonContainer>
                        <Tooltip
                          content={t(
                            "qtg.proposal.buttons.sendEmailInvoice.tooltip",
                            "Send invoice via email",
                          )}
                          relationship="label"
                        >
                          <Button
                            appearance="subtle"
                            size="medium"
                            icon={<MailArrowUpRegular />}
                            onClick={() => setIsInvoiceEmailDialogOpen(true)}
                            aria-label={t(
                              "qtg.proposal.documents.sendEmailInvoice",
                              "Send Invoice Email",
                            )}
                          />
                        </Tooltip>
                      </EmailButtonContainer>
                    </FileInfoHeader>
                  </FileInfoBox>
                )}
                {/* Invoice DOCX Document */}
                {invoiceDocxPath && (
                  <FileInfoBox>
                    <DocumentLink href={invoiceDocxPath} target="_blank">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="#2B579A"
                        style={{ marginRight: "8px", flexShrink: 0 }}
                      >
                        <path d="M4 4C4 2.89543 4.89543 2 6 2H10.5858C11.1162 2 11.6249 2.21071 12 2.58579L15.4142 6C15.7893 6.37507 16 6.88378 16 7.41421V16C16 17.1046 15.1046 18 14 18H6C4.89543 18 4 17.1046 4 16V4Z" />
                        <path
                          d="M11 2.5V6C11 6.55228 11.4477 7 12 7H15.5"
                          fill="white"
                        />
                        <text
                          x="10"
                          y="14"
                          fontSize="5"
                          fill="white"
                          textAnchor="middle"
                          fontWeight="bold"
                        >
                          DOCX
                        </text>
                      </svg>
                      {t(
                        "qtg.proposal.documents.invoiceDocx",
                        "Invoice (DOCX)",
                      )}
                    </DocumentLink>
                  </FileInfoBox>
                )}
                {/* Group invoice (one document for several offers of the payer), if this offer is part of one */}
                {offerId && (
                  <GroupInvoiceDocuments
                    offerId={offerId}
                    offerData={offerData}
                    getValues={getValues}
                    userInfo={userInfo}
                    onOfferDataUpdate={onOfferDataUpdate}
                  />
                )}
              </DocumentsGrid>
            ) : (
              <NoDocumentsMessage>
                {t(
                  "qtg.proposal.documents.noDocuments",
                  "No documents available yet",
                )}
              </NoDocumentsMessage>
            )}
            {offerData?.SentAt && offerData?.SentByUserFullName && (
              <EmailSentInfo>
                {t("qtg.proposal.documents.emailSentInfo", {
                  sentByUserFullName: offerData.SentByUserFullName,
                  sentByUsername: offerData.SentByUsername || "",
                  sentTo: offerData.SentTo || "",
                  sentAt: format(
                    new Date(offerData.SentAt),
                    "dd.MM.yyyy HH:mm",
                  ),
                })}
              </EmailSentInfo>
            )}
            {offerData?.Invoice?.Mtpl?.SentAt &&
              offerData?.Invoice?.Mtpl?.SentByUserFullName && (
                <EmailSentInfo>
                  {t("qtg.proposal.documents.invoiceSentInfo", {
                    sentByUserFullName:
                      offerData.Invoice.Mtpl.SentByUserFullName,
                    sentByUsername: offerData.Invoice.Mtpl.SentByUsername || "",
                    sentTo: offerData.Invoice.Mtpl.SentTo || "",
                    sentAt: format(
                      new Date(offerData.Invoice.Mtpl.SentAt),
                      "dd.MM.yyyy HH:mm",
                    ),
                  })}
                </EmailSentInfo>
              )}
          </DocumentsSection>
        )}

        {/* Insurance Data Section */}
        <InsuranceDataSection
          getError={getError}
          disabled={
            isSavedData ||
            !!offerId ||
            createOfferMutation.isPending ||
            calculationMutation.isPending
          }
        />

        {/* 1. Selection Section for Insurers and Periods - Hidden after offer is created (Get Quotes) */}
        {(!offerId || offerId === "") && (
          <>
            <SelectionSection>
              {/* Insurers Panel */}
              <SelectionPanel data-help="offer-insurers-panel">
                <SelectionLabelRow>
                  <SelectionLabel>
                    <ShieldRegular
                      style={{
                        fontSize: "16px",
                        color: "#d83b01",
                        marginRight: "8px",
                        verticalAlign: "middle",
                      }}
                    />
                    {t(
                      "qtg.proposal.mtplQuotes.selectInsurers",
                      "Choose MTPL Insurers",
                    )}{" "}
                    *
                  </SelectionLabel>
                  <SelectionActions>
                    <Tooltip
                      content={t("qtg.proposal.selectAll", "Select All")}
                      relationship="label"
                    >
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<CheckmarkRegular />}
                        disabled={
                          createOfferMutation.isPending ||
                          calculationMutation.isPending ||
                          isCalculating ||
                          saveOfferMutation.isPending
                        }
                        onClick={() => {
                          const allInsurers = MTPL_INSURERS.filter(
                            (i) => i.id !== "Gjensidige",
                          ).map((i) => i.id);
                          console.log(
                            "MTPL - Select All insurers:",
                            allInsurers,
                          );
                          setValue(
                            "mtplSelections.selectedInsurers",
                            allInsurers,
                          );
                        }}
                      />
                    </Tooltip>
                    <Tooltip
                      content={t("qtg.proposal.clearAll", "Clear All")}
                      relationship="label"
                    >
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<DismissRegular />}
                        disabled={
                          createOfferMutation.isPending ||
                          calculationMutation.isPending ||
                          isCalculating ||
                          saveOfferMutation.isPending
                        }
                        onClick={() => {
                          console.log("MTPL - Clear All insurers");
                          setValue("mtplSelections.selectedInsurers", []);
                        }}
                      />
                    </Tooltip>
                  </SelectionActions>
                </SelectionLabelRow>
                <CheckboxList>
                  {MTPL_INSURERS.filter((a) => a.id !== "Gjensidige").map(
                    (insurer) => (
                      <div
                        key={insurer.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Checkbox
                          checked={selectedInsurers.includes(insurer.id)}
                          onChange={() => handleInsurerToggle(insurer.id)}
                          disabled={
                            createOfferMutation.isPending ||
                            calculationMutation.isPending ||
                            isCalculating ||
                            saveOfferMutation.isPending
                          }
                        />
                        <img
                          src={`/insurers/${insurer.id.toLowerCase()}.png`}
                          alt={insurer.displayName}
                          title={insurer.displayName}
                          style={{
                            height: "20px",
                            objectFit: "contain",
                            cursor: "pointer",
                          }}
                          onClick={() => handleInsurerToggle(insurer.id)}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </div>
                    ),
                  )}
                </CheckboxList>
              </SelectionPanel>

              {/* Periods Panel */}
              <SelectionPanel data-help="offer-periods-panel">
                <SelectionLabelRow>
                  <SelectionLabel>
                    {t(
                      "qtg.proposal.mtplQuotes.selectPeriods",
                      "Choose Periods",
                    )}{" "}
                    *
                  </SelectionLabel>
                  <SelectionActions>
                    <Tooltip
                      content={t("qtg.proposal.selectAll", "Select All")}
                      relationship="label"
                    >
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<CheckmarkRegular />}
                        disabled={
                          createOfferMutation.isPending ||
                          calculationMutation.isPending ||
                          isCalculating ||
                          saveOfferMutation.isPending
                        }
                        onClick={() => {
                          const allPeriods = QUOTE_PERIODS.map((p) => p.id);
                          console.log("MTPL - Select All periods:", allPeriods);
                          setValue(
                            "mtplSelections.selectedPeriods",
                            allPeriods,
                          );
                        }}
                      />
                    </Tooltip>
                    <Tooltip
                      content={t("qtg.proposal.clearAll", "Clear All")}
                      relationship="label"
                    >
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<DismissRegular />}
                        disabled={
                          createOfferMutation.isPending ||
                          calculationMutation.isPending ||
                          isCalculating ||
                          saveOfferMutation.isPending
                        }
                        onClick={() => {
                          console.log("MTPL - Clear All periods");
                          setValue("mtplSelections.selectedPeriods", []);
                        }}
                      />
                    </Tooltip>
                  </SelectionActions>
                </SelectionLabelRow>
                <CheckboxList>
                  {QUOTE_PERIODS.map((period) => (
                    <Checkbox
                      key={period.id}
                      checked={selectedPeriods.includes(period.id)}
                      onChange={() => handlePeriodToggle(period.id)}
                      disabled={
                        createOfferMutation.isPending ||
                        calculationMutation.isPending ||
                        isCalculating ||
                        saveOfferMutation.isPending
                      }
                      label={period.label}
                    />
                  ))}
                </CheckboxList>
              </SelectionPanel>
            </SelectionSection>

            {/* Validation Summary */}
            {validationErrors.length > 0 && (
              <ValidationSummary
                errors={validationErrors}
                title="Please fix these issues before calculating MTPL quotes:"
                onErrorClick={() => scrollToFirstError()}
                showWarnings={true}
              />
            )}
          </>
        )}

        {/* 2. Quotes Table - Shown after response, above buttons */}
        {quotes.length > 0 && (
          <QuotesWrapper data-help="offer-quotes-table">
            <QuoteErrorBoundary
              fallbackTitle="MTPL Quotes Display Error"
              fallbackMessage="An error occurred while displaying MTPL quotes. The quote data may be invalid or incomplete."
            >
              <MtplQuotesDisplay
                quotes={createQuotes}
                savedQuotes={saveQuotes}
                requestedInsurers={requestedMtplInsurers}
                requestedPeriods={requestedMtplPeriods}
                invoiceData={mtplInvoiceData}
                onSelectionChange={handleQuoteSelectionChange}
                updatedQuotes={mtplUpdated}
                onQuoteUpdate={handleQuoteUpdate}
                excludedItems={mtplExcluded}
                onExclusionChange={handleExclusionChange}
                canEdit={!!offerId && !invoicePdfPath}
              />
            </QuoteErrorBoundary>

            {/* Debug: Log updatedQuotes being passed */}
            {console.log(
              "🎨 RENDER: Passing updatedQuotes to MtplQuotesDisplay:",
              mtplUpdated,
            )}
          </QuotesWrapper>
        )}
      </ScrollableContent>

      {/* Footer Panel with Action Buttons - Always visible */}
      <FooterPanel>
        {/* Action buttons on the right */}
        <ButtonGroup>
          {/* Preview Quotes Button - Only shown when no offerId and hideCalculateButton is false */}
          {!hideCalculateButton && (!offerId || offerId === "") && (
            <Tooltip
              content={t(
                "qtg.proposal.mtplQuotes.previewTooltip",
                "Preview quotes without creating an offer. Results will not be saved.",
              )}
              relationship="description"
            >
              <Button
                data-help="offer-preview-button"
                appearance="secondary"
                onClick={handleCalculate}
                disabled={
                  calculationMutation.isPending ||
                  createOfferMutation.isPending ||
                  isCalculating ||
                  saveOfferMutation.isPending ||
                  selectedInsurers.length === 0 ||
                  selectedPeriods.length === 0 ||
                  !isValid
                }
                icon={<InfoRegular />}
              >
                {calculationMutation.isPending ? (
                  <>
                    <Spinner size="tiny" style={{ marginRight: "8px" }} />
                    {t("qtg.proposal.buttons.previewing", "Previewing...")}
                  </>
                ) : (
                  t("qtg.proposal.buttons.previewQuotes", "Preview Quotes")
                )}
              </Button>
            </Tooltip>
          )}

          {/* Create Offer Button - Only shown when no offerId and no pending updates */}
          {(!offerId || offerId === "") && mtplUpdated.length === 0 && (
            <Tooltip
              content={t(
                "qtg.proposal.mtplQuotes.createOfferTooltip",
                "Create offer and generate documents. This will save the proposal to the database.",
              )}
              relationship="description"
            >
              <Button
                data-help="offer-create-offer-button"
                appearance="primary"
                onClick={handleGetQuotes}
                disabled={
                  createOfferMutation.isPending ||
                  calculationMutation.isPending ||
                  isCalculating ||
                  saveOfferMutation.isPending ||
                  selectedInsurers.length === 0 ||
                  selectedPeriods.length === 0 ||
                  !isValid
                }
              >
                {createOfferMutation.isPending ? (
                  <>
                    <Spinner
                      size="extra-small"
                      style={{ marginRight: "8px" }}
                    />
                    {t(
                      "qtg.proposal.buttons.creatingOffer",
                      "Creating Offer...",
                    )}
                  </>
                ) : (
                  t("qtg.proposal.buttons.createOffer", "Create Offer")
                )}
              </Button>
            </Tooltip>
          )}

          {/* Update Offer Button - Shown when there are NEW pending updates */}
          {offerId &&
            quotes.length > 0 &&
            hasNewPendingChanges &&
            !invoicePdfPath && (
              <Tooltip
                content={t(
                  "qtg.proposal.buttons.updateOffer.tooltip",
                  "Send updated quote values to backend and regenerate documents",
                )}
                relationship="label"
              >
                <Button
                  data-help="offer-update-button"
                  appearance="primary"
                  onClick={handleUpdateOffer}
                  disabled={
                    updateOfferMutation.isPending ||
                    createOfferMutation.isPending ||
                    calculationMutation.isPending ||
                    isCalculating ||
                    saveOfferMutation.isPending
                  }
                >
                  {updateOfferMutation.isPending ? (
                    <>
                      <Spinner
                        size="extra-small"
                        style={{ marginRight: "8px" }}
                      />
                      {t("qtg.proposal.buttons.updating", "Updating...")}
                    </>
                  ) : (
                    t("qtg.proposal.buttons.updateOffer", "Update Offer")
                  )}
                </Button>
              </Tooltip>
            )}

          {/* Save Offer Button - Only shown when offerId exists, quotes exist, and no pending changes */}
          {offerId && quotes.length > 0 && !hasNewPendingChanges && (
            <>
              {/* Create Invoice Button - Shown after offer is created, hidden when invoice exists or NEW pending updates */}
              {!invoicePdfPath && (
                <Tooltip
                  content={
                    !selectedQuoteForInvoice
                      ? t(
                          "qtg.proposal.buttons.createInvoice.selectQuote",
                          "Please select a quote first",
                        )
                      : t(
                          "qtg.proposal.buttons.createInvoice.tooltip",
                          "Create invoice document for the selected quote",
                        )
                  }
                  relationship="label"
                >
                  <Button
                    data-help="offer-create-invoice-button"
                    appearance="secondary"
                    icon={<ReceiptMoneyRegular />}
                    onClick={() => setIsInvoiceDialogOpen(true)}
                    disabled={
                      createOfferMutation.isPending ||
                      calculationMutation.isPending ||
                      isCalculating ||
                      saveOfferMutation.isPending ||
                      !selectedQuoteForInvoice
                    }
                  >
                    {t("qtg.proposal.buttons.createInvoice", "Create Invoice")}
                  </Button>
                </Tooltip>
              )}

              {/* Save Offer Button - Hidden when invoice exists */}
              {!invoicePdfPath && (
                <Tooltip
                  content={t(
                    "qtg.proposal.buttons.saveProposal.tooltip",
                    "Save the selected quotes to the offer",
                  )}
                  relationship="label"
                >
                  <Button
                    data-help="offer-save-offer-button"
                    appearance="primary"
                    onClick={handleSaveOffer}
                    disabled={
                      createOfferMutation.isPending ||
                      calculationMutation.isPending ||
                      isCalculating ||
                      saveOfferMutation.isPending
                      // //||
                      // (offerData?.Offer?.Response?.MtplSaved &&
                      //   offerData.Offer.Response.MtplSaved.some(
                      //     (s) => s.PolicyNumber
                      //   )) ||
                      // (offerData?.Offer?.Response?.CascoSaved &&
                      //   offerData.Offer.Response.CascoSaved.some(
                      //     (s) => s.PolicyNumber
                      //   ))
                    }
                  >
                    {saveOfferMutation.isPending ? (
                      <>
                        <Spinner
                          size="extra-small"
                          style={{ marginRight: "8px" }}
                        />
                        {t("qtg.proposal.buttons.saving", "Saving...")}
                      </>
                    ) : (
                      t("qtg.proposal.buttons.saveProposal", "Save Proposal")
                    )}
                  </Button>
                </Tooltip>
              )}
            </>
          )}
        </ButtonGroup>
      </FooterPanel>

      {/* Error Details Dialog */}
      <ErrorDetailsDialog
        open={isErrorDialogOpen}
        onOpenChange={setIsErrorDialogOpen}
        quotes={mergedErrorDataForDialog}
        title="MTPL Quote Errors and Details"
        showBonusMalus={true}
      />

      {/* Send Email Dialog */}
      {offerId && offerId !== "" && (
        <SendEmailDialog
          open={isEmailDialogOpen}
          onClose={() => setIsEmailDialogOpen(false)}
          offerId={offerId}
          defaultAttachment={
            savedPdfPath || offerPdfPath
              ? {
                  name: savedPdfPath
                    ? t(
                        "qtg.proposal.documents.savedOfferPdf",
                        "Saved Offer (PDF)",
                      )
                    : t("qtg.proposal.documents.offerPdf", "Offer (PDF)"),
                  url: savedPdfPath || offerPdfPath || "",
                }
              : undefined
          }
          template={MTPL_EMAIL_TEMPLATE}
          templateData={{
            Prefix:
              getValues("clientData.clientType") === "person" ? "Cien." : "",
            ClientName:
              getValues("clientData.clientType") === "person"
                ? `${getValues("clientData.clientFirstName") || ""} ${
                    getValues("clientData.clientLastName") || ""
                  }`.trim()
                : getValues("clientData.clientName") || "",
            MarkModel: `${getValues("vehicleData.vehicleMake") || ""} ${
              getValues("vehicleData.vehicleModel") || ""
            }`.trim(),
            PlateNo: getValues("vehicleData.licensePlateNumber") || "",
            PolicyEndDate:
              formatDateTimeString(
                offerData?.Offer?.Request?.PreviousInsuranceEndDate,
              ) || "",
            ServicePersonName:
              userInfo?.userFullName || userInfo?.name || "XXX",
            ServicePersonPhone:
              userInfo?.userPhoneNumber || DEFAULT_SERVICE_PERSON_PHONE,
            ServicePersonEMail:
              userInfo?.userEmail || userInfo?.email || "xxx@greco.eu",
          }}
          defaultFromName={userInfo?.userFullName || userInfo?.name || ""}
          defaultFromEmail={userInfo?.userEmail || userInfo?.email || ""}
          defaultToEmail={getValues("clientData.clientEmail") || ""}
          onEmailSent={(updatedOffer) => {
            if (onOfferDataUpdate) {
              onOfferDataUpdate(updatedOffer);
            }
          }}
        />
      )}

      {/* Send Invoice Email Dialog */}
      {offerId && offerId !== "" && invoicePdfPath && (
        <SendEmailDialog
          open={isInvoiceEmailDialogOpen}
          onClose={() => setIsInvoiceEmailDialogOpen(false)}
          offerId={offerId}
          invoiceId={offerData?.Invoice?.Mtpl?.Response?.InvoiceId || undefined}
          defaultAttachment={
            invoicePdfPath
              ? {
                  name: t("qtg.proposal.documents.invoicePdf", "Invoice (PDF)"),
                  url: invoicePdfPath || "",
                }
              : undefined
          }
          template={MTPL_INVOICE_EMAIL_TEMPLATE}
          templateData={{
            Prefix: invoiceEmailAddressee.Prefix,
            ClientName: invoiceEmailAddressee.ClientName,
            MarkModel: `${getValues("vehicleData.vehicleMake") || ""} ${
              getValues("vehicleData.vehicleModel") || ""
            }`.trim(),
            PlateNo: getValues("vehicleData.licensePlateNumber") || "",
            PolicyEndDate:
              formatDateTimeString(
                offerData?.Offer?.Request?.PreviousInsuranceEndDate,
              ) || "",
            ServicePersonName:
              userInfo?.userFullName || userInfo?.name || "XXX",
            ServicePersonPhone:
              userInfo?.userPhoneNumber || DEFAULT_SERVICE_PERSON_PHONE,
            ServicePersonEMail:
              userInfo?.userEmail || userInfo?.email || "xxx@greco.eu",
          }}
          defaultFromName={userInfo?.userFullName || userInfo?.name || ""}
          defaultFromEmail={userInfo?.userEmail || userInfo?.email || ""}
          defaultToEmail={invoiceEmailAddressee.email}
          onEmailSent={(updatedOffer) => {
            if (onOfferDataUpdate) {
              onOfferDataUpdate(updatedOffer);
            }
          }}
        />
      )}

      {/* Create Invoice Dialog */}
      {offerId && offerId !== "" && (
        <CreateInvoiceDialog
          open={isInvoiceDialogOpen}
          onClose={() => setIsInvoiceDialogOpen(false)}
          offerId={offerId}
          insuranceLine="mtpl"
          selectedQuote={
            selectedQuoteForInvoice
              ? {
                  ...selectedQuoteForInvoice,
                  variantIndex: 0, // MTPL doesn't have variants, use 0
                }
              : undefined
          }
          isSavedOffer={isSavedData}
          policyNumber={undefined} // MTPL policy number not tracked yet
          client={offerData?.Offer?.Request?.Client}
          onOfferUpdate={onOfferDataUpdate}
        />
      )}
    </TabWrapper>
  );
};

export default MtplQuotesTab;
